import environment from "environment";
import axios, { AxiosRequestConfig, AxiosResponse, Method } from "axios";
import HttpErrorResponseModel from "../models/HttpErrorResponseModel";

export enum RequestMethod {
    Get = "GET",
    Post = "POST",
    Put = "PUT",
    Delete = "DELETE",
    Options = "OPTIONS",
    Head = "HEAD",
    Patch = "PATCH",
}

export default class HttpUtility {
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    private constructor() {}

    private static isTokenRefreshing: boolean = false;
    private static tokenRefreshSubscribers: (() => void)[] = [];

    public static async get(
        endpoint: string,
        params?: any,
        requestConfig?: AxiosRequestConfig
    ): Promise<AxiosResponse | HttpErrorResponseModel> {
        const paramsConfig: AxiosRequestConfig | undefined = params ? { params } : undefined;

        return HttpUtility._request(
            {
                url: endpoint,
                method: RequestMethod.Get,
            },
            {
                ...paramsConfig,
                ...requestConfig,
            }
        );
    }

    public static async post(
        endpoint: string,
        data?: any,
        requestConfig?: AxiosRequestConfig
    ): Promise<AxiosResponse | HttpErrorResponseModel> {
        const config: AxiosRequestConfig | undefined = data ? { data } : undefined;

        return HttpUtility._request(
            {
                url: endpoint,
                method: RequestMethod.Post,
            },
            {
                ...config,
                ...requestConfig,
            }
        );
    }

    public static async put(endpoint: string, data?: any): Promise<AxiosResponse | HttpErrorResponseModel> {
        const config: AxiosRequestConfig | undefined = data ? { data } : undefined;

        return HttpUtility._request(
            {
                url: endpoint,
                method: RequestMethod.Put,
            },
            config
        );
    }

    public static async patch(endpoint: string, data?: any): Promise<AxiosResponse | HttpErrorResponseModel> {
        const config: AxiosRequestConfig | undefined = data ? { data } : undefined;

        return HttpUtility._request(
            {
                url: endpoint,
                method: RequestMethod.Patch,
            },
            config
        );
    }

    public static async delete(
        endpoint: string,
        params?: any,
        requestConfig?: AxiosRequestConfig
    ): Promise<AxiosResponse | HttpErrorResponseModel> {
        const paramsConfig: AxiosRequestConfig | undefined = params ? { params } : undefined;

        return HttpUtility._request(
            {
                url: endpoint,
                method: RequestMethod.Delete,
            },
            {
                ...paramsConfig,
                ...requestConfig,
            }
        );
    }

    public static async deletePost(
        endpoint: string,
        data?: any,
        requestConfig?: AxiosRequestConfig
    ): Promise<AxiosResponse | HttpErrorResponseModel> {
        const config: AxiosRequestConfig | undefined = data ? { data } : undefined;

        return HttpUtility._request(
            {
                url: endpoint,
                method: RequestMethod.Delete,
            },
            {
                ...config,
                ...requestConfig,
            }
        );
    }

    private static async _request(
        restRequest: Partial<Request>,
        config?: AxiosRequestConfig
    ): Promise<AxiosResponse | HttpErrorResponseModel> {
        if (!Boolean(restRequest.url)) {
            console.error(`Received ${restRequest.url} which is invalid for a endpoint url`);
        }

        const token = sessionStorage.getItem(environment.accessTokenName);

        if (token) {
            if (!config) {
                config = {};
            }

            config.headers = {
                ...config.headers,
                Authorization: `Bearer ${token}`,
            };
        }

        try {
            const axiosRequestConfig: AxiosRequestConfig = {
                ...config,
                method: restRequest.method as Method,
                url: restRequest.url,
                headers: {
                    // "Content-Type": "application/x-www-form-urlencoded",
                    "Content-Type": "application/json",
                    "Accept-Language": "it",
                    "Cache-Control": "no-cache",
                    "x-api-key": environment.tenant, //-- chiave inserita per logica multitenant
                    ...config?.headers,
                },
            };
            const [axiosResponse] = await Promise.all([axios(axiosRequestConfig), HttpUtility._delay()]);

            const { status, data, request } = axiosResponse;

            if (data.success === false) {
                return HttpUtility._fillInErrorWithDefaults(
                    {
                        status,
                        message: data.errors.join(" - "),
                        errors: data.errors,
                        url: request ? request.responseURL : restRequest.url,
                        raw: axiosResponse,
                    },
                    restRequest
                );
            }

            return {
                ...axiosResponse,
            };
        } catch (error) {
            if (error.response) {
                // The request was made and the server responded with a status code that falls out of the range of 2xx
                const { status, statusText, data } = error.response;

                // if error is 401 and is not a "try again" call
                if (status === 401 && config?.headers?.retry !== true) {
                    // if a refresh token call is not in progress
                    if (HttpUtility.isTokenRefreshing === false) {
                        // set in progress refresh token call
                        HttpUtility.isTokenRefreshing = true;

                        const accessToken = sessionStorage.getItem(environment.accessTokenName);
                        const refreshToken = sessionStorage.getItem(environment.refreshTokenName);
                        // call refreshToken API to get new accessToken and refreshToken (with "try again" identifier)
                        HttpUtility.post(
                            environment.api.refreshToken,
                            { accessToken: accessToken, refreshToken: refreshToken },
                            { headers: { retry: true } }
                        ).then((response: AxiosResponse<any> | HttpErrorResponseModel) => {
                            if (
                                !(response instanceof HttpErrorResponseModel) &&
                                response.hasOwnProperty("data") &&
                                response.data.hasOwnProperty("accessToken") &&
                                response.data.hasOwnProperty("refreshToken")
                            ) {
                                // if response is not an error and contain accessToken and refreshToken
                                // replace accessToken and refreshToken in sessionStorage
                                sessionStorage.setItem(environment.accessTokenName, response.data.accessToken);
                                sessionStorage.setItem(environment.refreshTokenName, response.data.refreshToken);
                            }

                            // set not in progress refresh token call
                            HttpUtility.isTokenRefreshing = false;

                            // redo original calls (gone in 401 error)
                            HttpUtility.tokenRefreshSubscribers.map((callback) => callback());

                            // empty original calls (gone in 401 error)
                            HttpUtility.tokenRefreshSubscribers = [];
                        });
                    }

                    // add original call to redo after refresh token call
                    const retryResponse = (await new Promise((resolve) => {
                        HttpUtility.tokenRefreshSubscribers.push(() => {
                            resolve(
                                HttpUtility._request(restRequest, {
                                    ...config,
                                    headers: {
                                        ...config?.headers,
                                        retry: true,
                                    },
                                })
                            );
                        });
                    })) as AxiosResponse | HttpErrorResponseModel;

                    // return redone call response
                    return retryResponse;
                }

                const errors: string[] = data.hasOwnProperty("errors") ? [statusText, ...data.errors] : [statusText];

                return HttpUtility._fillInErrorWithDefaults(
                    {
                        status,
                        message: errors.filter(Boolean).join(" - "),
                        errors,
                        url: error.request.responseURL,
                        raw: error.response,
                    },
                    restRequest
                );
            } else if (error.request) {
                // The request was made but no response was received `error.request` is an instance of XMLHttpRequest in the browser and an instance of http.ClientRequest in node.js
                const { status, statusText, responseURL } = error.request;

                return HttpUtility._fillInErrorWithDefaults(
                    {
                        status,
                        message: statusText,
                        errors: [statusText],
                        url: responseURL,
                        raw: error.request,
                    },
                    restRequest
                );
            }

            // Something happened in setting up the request that triggered an Error
            return HttpUtility._fillInErrorWithDefaults(
                {
                    status: 0,
                    message: error.message,
                    errors: [error.message],
                    url: restRequest.url!,
                    raw: error,
                },
                restRequest
            );
        }
    }

    private static _fillInErrorWithDefaults(
        error: Partial<HttpErrorResponseModel>,
        request: Partial<Request>
    ): HttpErrorResponseModel {
        const model = new HttpErrorResponseModel();

        model.status = error.status || 0;
        model.message = error.message || "Error requesting data";
        model.errors = error.errors!.length ? error.errors! : ["Error requesting data"];
        model.url = error.url || request.url!;
        model.raw = error.raw;

        // Remove anything with undefined or empty strings.
        model.errors = model.errors.filter(Boolean);

        return model;
    }

    /**
     * We want to show the loading indicator to the user but sometimes the api
     * request finished too quickly. This makes sure there the loading indicator is
     * visual for at least a given time.
     *
     * @param duration
     * @returns {Promise<unknown>}
     * @private
     */
    private static _delay(duration: number = 250): Promise<void> {
        return new Promise((resolve) => setTimeout(resolve, duration));
    }
}
